/**
 * FractionalQtyInput.jsx (iter149) — Numeric input that respects per-SKU
 * `allow_fractional` + `unit_of_measure` settings.
 *
 * Usage:
 *   <FractionalQtyInput
 *     sku={selectedSku}              // pass either sku or variant — see helpers
 *     value={comp.quantity}
 *     onChange={(num) => updateComponent(idx, 'quantity', num)}
 *     data-testid="bom-comp-qty-0"
 *   />
 *
 * Behaviour:
 *   • sku.allow_fractional=true  → step=0.01, parseFloat, shows UoM suffix
 *   • sku.allow_fractional=false → step=1,    parseInt,   no suffix
 *   • sku=null/undefined         → integer default (safe fallback)
 */
import React from 'react';
import { Input } from './ui/input';

export const formatQty = (n, sku) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const fractional = !!(sku && sku.allow_fractional);
  const body = fractional ? num.toFixed(2) : String(Math.round(num));
  const uom = sku?.unit_of_measure;
  if (uom && uom !== 'each') return `${body} ${uom}`;
  return body;
};

export const FractionalQtyInput = ({ sku, value, onChange, ...rest }) => {
  const fractional = !!(sku && sku.allow_fractional);
  const uom = sku?.unit_of_measure;
  const step = fractional ? 0.01 : 1;
  const showSuffix = fractional && uom && uom !== 'each';

  return (
    <div className="relative">
      <Input
        type="number"
        inputMode={fractional ? 'decimal' : 'numeric'}
        min={0}
        step={step}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') { onChange(''); return; }
          const parsed = fractional ? parseFloat(raw) : parseInt(raw, 10);
          if (Number.isNaN(parsed)) { onChange(''); return; }
          onChange(fractional ? Math.round(parsed * 100) / 100 : parsed);
        }}
        className={showSuffix ? 'pr-10' : ''}
        {...rest}
      />
      {showSuffix && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground pointer-events-none"
          data-testid={(rest['data-testid'] ? `${rest['data-testid']}-uom` : 'qty-uom')}
        >
          {uom}
        </span>
      )}
    </div>
  );
};

export default FractionalQtyInput;
